<template>
  <b-container fluid class="py-3">
    <h4 class="mb-1">Integracje urzędowe</h4>
    <p class="text-muted">
      Podgląd tego, co system potrafi dziś po stronie ZUS i NFZ. Zwolnienie
      wystawia się przy wizycie pacjenta, tutaj są czynności obsługowe:
      pula numerów na wypadek awarii i anulowanie już wystawionego dokumentu.
    </p>

    <!-- ============================ ZUS ============================ -->
    <b-card class="mb-4">
      <template #header>
        <strong>ZUS — zwolnienia lekarskie (e-ZLA)</strong>
        <b-badge v-if="srodowisko" :variant="srodowisko === 'produkcja' ? 'danger' : 'secondary'" class="ml-2">
          {{ srodowisko === 'produkcja' ? 'PRODUKCJA' : 'symulator' }}
        </b-badge>
      </template>

      <!--
        Jedno pole hasła na całą sekcję. Każda rozmowa z ZUS wymaga podpisu
        lekarza, więc bez hasła nie da się nawet ODCZYTAĆ puli numerów.
        Hasła nie zapamiętujemy: po każdej operacji pole jest czyszczone.
      -->
      <b-form-row class="align-items-end mb-3">
        <b-col md="4">
          <label class="mb-1">Hasło do certyfikatu lekarza</label>
          <b-form-input v-model="haslo" type="password" autocomplete="off" :disabled="zajety" />
        </b-col>
        <b-col md="3">
          <label class="mb-1">
            Lekarz (USE_Id)
            <small class="text-muted">puste = certyfikat z konfiguracji serwera</small>
          </label>
          <b-form-input v-model="useId" :disabled="zajety" />
        </b-col>
        <b-col md="5">
          <small class="text-muted d-block">
            Zwolnienia podpisuje się ręcznie przy każdym dokumencie. Inaczej niż
            recepty, hasło nie jest brane z bazy i nie zostaje po operacji.
          </small>
        </b-col>
      </b-form-row>

      <b-alert v-if="bladZus" show variant="warning">{{ bladZus }}</b-alert>

      <b-tabs content-class="pt-3">
        <!-- --------------------------- pula --------------------------- -->
        <b-tab title="Pula numerów" active>
          <p class="text-muted">
            Numery rezerwowane w ZUS z wyprzedzeniem. Gdy ZUS nie odpowiada,
            lekarz wypisuje druk papierowy z numerem z puli i elektronizuje go
            po powrocie łączności, w ciągu trzech dni roboczych. Rezerwacja też
            idzie przez ZUS, więc w chwili awarii jest już za późno.
          </p>
          <b-button variant="outline-primary" :disabled="!haslo || zajety" @click="pobierzPule()">
            Sprawdź stan puli
          </b-button>
          <b-input-group class="mt-2" style="max-width: 320px">
            <b-form-input v-model.number="ileNumerow" type="number" min="1" max="150" :disabled="zajety" />
            <b-input-group-append>
              <b-button variant="outline-secondary" :disabled="!haslo || zajety" @click="pobierzPule(true)">
                Zarezerwuj numery
              </b-button>
            </b-input-group-append>
          </b-input-group>

          <div v-if="pula" class="mt-3">
            <b-alert v-if="pula.wymaga_uzupelnienia" show variant="warning" class="py-2">
              Pula na wyczerpaniu. Dobierz numery, zanim będą potrzebne.
            </b-alert>
            <p class="mb-1">
              Wolnych: <strong>{{ pula.liczby.wolne }}</strong>,
              wypisanych na papierze: <strong>{{ pula.liczby.uzyte_offline }}</strong>,
              wszystkich w ZUS: {{ pula.liczby.wszystkich_w_zus }}
            </p>
            <p v-if="pula.zarezerwowano" class="text-success mb-1">
              Zarezerwowano: {{ pula.zarezerwowano.join(', ') }}
            </p>
            <details v-if="pula.wolne.length">
              <summary>Wolne numery ({{ pula.wolne.length }})</summary>
              <code class="small">{{ pula.wolne.join(', ') }}</code>
            </details>
            <b-alert v-if="pula.nierozpoznane && pula.nierozpoznane.length" show variant="secondary" class="py-2 mt-2">
              <!--
                Statusy druku, których znaczenia ZUS nigdzie nie opisał.
                Świadomie nie liczymy ich jako wolnych: lepiej stracić numer
                niż wydać ten sam dwa razy.
              -->
              {{ pula.nierozpoznane.length }} druków ma status o nieznanym znaczeniu.
              Nie są traktowane jako wolne.
            </b-alert>
          </div>
        </b-tab>

        <!-- ------------------------ anulowanie ------------------------ -->
        <b-tab title="Anulowanie zwolnienia">
          <p class="text-muted">
            Wystawionego zwolnienia nie da się poprawić. Jedyna droga korekty to
            anulowanie i wystawienie nowego dokumentu.
          </p>
          <b-form-row>
            <b-col md="3">
              <label class="mb-1">Seria i numer</label>
              <b-form-input v-model="anul.seriaNumer" placeholder="np. ZY9852453" :disabled="zajety" />
            </b-col>
            <b-col md="4">
              <label class="mb-1">Przyczyna</label>
              <b-form-select v-model="anul.kod" :options="przyczynyOpcje" :disabled="zajety || !przyczyny.length" />
              <b-button
                v-if="!przyczyny.length"
                size="sm"
                variant="link"
                class="px-0"
                :disabled="!haslo || zajety"
                @click="pobierzSlowniki()"
              >
                Wczytaj listę przyczyn z ZUS
              </b-button>
            </b-col>
            <b-col md="5">
              <label class="mb-1">Opis dla ZUS</label>
              <b-form-input v-model="anul.opis" :disabled="zajety" />
            </b-col>
          </b-form-row>
          <b-form-checkbox v-model="anul.notyfikuj" class="mt-2" :disabled="zajety">
            Powiadom ubezpieczonego i płatnika
          </b-form-checkbox>
          <b-button
            variant="danger"
            class="mt-2"
            :disabled="!haslo || !anul.seriaNumer || !anul.kod || !anul.opis || zajety"
            @click="anuluj()"
          >
            Anuluj zwolnienie w ZUS
          </b-button>
          <b-alert v-if="wynikAnul" show variant="success" class="mt-3">
            Anulowano {{ wynikAnul.seria_numer }} (przyczyna {{ wynikAnul.przyczyna.kod }}).
            Poświadczeń z ZUS: {{ wynikAnul.upp }}.
          </b-alert>
        </b-tab>

        <!-- ------------------------- słowniki ------------------------- -->
        <b-tab title="Słownik przyczyn">
          <b-button variant="outline-primary" :disabled="!haslo || zajety" @click="pobierzSlowniki()">
            Pobierz z ZUS
          </b-button>
          <b-table
            v-if="przyczyny.length"
            class="mt-3"
            small
            striped
            :items="przyczyny"
            :fields="[
              { key: 'kod', label: 'Kod' },
              { key: 'nazwa', label: 'Nazwa' },
              { key: 'opis', label: 'Opis' }
            ]"
          />
        </b-tab>
      </b-tabs>
    </b-card>

    <!-- ============================ NFZ ============================ -->
    <b-card>
      <template #header><strong>NFZ — kontrakt i sprawozdawczość</strong></template>
      <b-alert v-if="bladNfz" show variant="warning">{{ bladNfz }}</b-alert>
      <b-button variant="outline-primary" :disabled="zajety" @click="pobierzNfz()">
        Sprawdź stan NFZ
      </b-button>

      <div v-if="nfz" class="mt-3">
        <p class="mb-1">
          Tryb NFZ:
          <b-badge :variant="nfz.aktywny ? 'success' : 'secondary'">
            {{ nfz.aktywny ? 'włączony' : 'wyłączony' }}
          </b-badge>
        </p>
        <!--
          Trybu NFZ nie da się włączyć bez zaimportowanej umowy — pilnuje tego
          serwer, nie ten ekran. Kolejność jest wymuszona: najpierw import UMX,
          potem tryb.
        -->
        <p class="text-muted mb-2">
          Tryb można włączyć dopiero po zaimportowaniu umowy. Rozdział pracy
          na NFZ i prywatnie idzie po harmonogramie komórki, nie po placówce.
        </p>
        <pre class="small bg-light p-2">{{ JSON.stringify(nfz, null, 2) }}</pre>
      </div>
      <div v-if="nfzPule" class="mt-3">
        <strong>Spalanie kontraktu</strong>
        <pre class="small bg-light p-2">{{ JSON.stringify(nfzPule, null, 2) }}</pre>
      </div>
    </b-card>
  </b-container>
</template>

<script>
/**
 * Ekran przeglądowy integracji urzędowych.
 *
 * PO CO OSOBNY EKRAN, SKORO ZWOLNIENIE WYSTAWIA SIĘ PRZY WIZYCIE:
 * bo pula numerów i anulowanie nie należą do wizyty. Pulę uzupełnia się
 * raz na jakiś czas, a anuluje się dokument sprzed tygodnia, często przy
 * innym pacjencie na ekranie.
 *
 * REGUŁY LICZY MEDAPI. Ten komponent nie zna ani jednego przepisu ZUS
 * i NFZ — wysyła żądanie i pokazuje odpowiedź.
 */
import ezla from '@/services/ezla'

export default {
  name: 'IntegracjeZusNfz',
  data() {
    return {
      haslo: '',
      useId: '',
      zajety: false,
      srodowisko: '',
      bladZus: '',
      bladNfz: '',
      ileNumerow: 10,
      pula: null,
      przyczyny: [],
      anul: { seriaNumer: '', kod: '', opis: '', notyfikuj: false },
      wynikAnul: null,
      nfz: null,
      nfzPule: null
    }
  },
  computed: {
    przyczynyOpcje() {
      return this.przyczyny.map((p) => ({ value: p.kod, text: `${p.kod} — ${p.nazwa}` }))
    }
  },
  methods: {
    /** Wspólna obsługa: jedno miejsce na hasło, błędy i czyszczenie. */
    async doZus(wywolanie, dodatkowe = {}) {
      this.zajety = true
      this.bladZus = ''
      try {
        const { data } = await wywolanie({
          haslo_certyfikatu: this.haslo,
          ...(this.useId ? { use_id: this.useId } : {}),
          ...dodatkowe
        })
        if (data.srodowisko) this.srodowisko = data.srodowisko
        return data
      } catch (e) {
        // Pokazujemy PRAWDZIWY powód z serwera. Komunikaty z medapi są pisane
        // dla człowieka i mówią, co zrobić dalej.
        const odp = e && e.response && e.response.data
        this.bladZus = (odp && odp.message) || (e && e.message) || 'Nie udało się połączyć z ZUS.'
        return null
      } finally {
        // Hasło żyje przez jedną operację, także w pamięci przeglądarki.
        this.haslo = ''
        this.zajety = false
      }
    },
    async pobierzPule(rezerwuj = false) {
      const d = await this.doZus(ezla.pula, rezerwuj ? { ile: this.ileNumerow } : {})
      if (d) this.pula = d
    },
    async pobierzSlowniki() {
      const d = await this.doZus(ezla.slowniki)
      if (d) this.przyczyny = d.przyczyny_anulowania || []
    },
    async anuluj() {
      this.wynikAnul = null
      const d = await this.doZus(ezla.anuluj, {
        seria_numer: this.anul.seriaNumer,
        kod_przyczyny: this.anul.kod,
        opis_przyczyny: this.anul.opis,
        notyfikuj: this.anul.notyfikuj
      })
      if (d) this.wynikAnul = d
    },
    async pobierzNfz() {
      this.zajety = true
      this.bladNfz = ''
      try {
        const stan = await ezla.nfzStan()
        this.nfz = stan.data
        try {
          const pule = await ezla.nfzPule()
          this.nfzPule = pule.data
        } catch (e) {
          // Brak pul nie jest błędem: tenant może nie mieć jeszcze umowy.
          this.nfzPule = null
        }
      } catch (e) {
        const odp = e && e.response && e.response.data
        this.bladNfz = (odp && odp.message) || 'Nie udało się pobrać stanu NFZ.'
      } finally {
        this.zajety = false
      }
    }
  }
}
</script>
